import { Link, NavLink, Outlet, useLocation, useNavigationType } from 'react-router';
import { Clapperboard, Headphones } from 'lucide-react';
import { MinaretIcon, MosqueIcon, MushafIcon, TasbihIcon } from '@/components/islamic/Ornaments';
import { MiniPlayer } from '@/features/player/PlayerUI';
import { useApplyTheme } from '@/features/settings/theme';
import { useNotificationSync } from '@/lib/push';
import { useCloudSync } from '@/lib/sync';
import { haptic, releaseStaleScrollLock } from '@/components/ui';
import { usePlayAdhan } from './adhan';
import { useNamePlace } from '@/features/prayer/Prayer';
import { restoreHifzRepeat } from '@/features/hifz/HifzPages';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { SCROLL_ID, scrollPageTo, scrollTop, scroller } from '@/lib/scroll';
import { useUpdate } from '@/lib/updates';
import { trackResumePoints } from '@/features/player/resume';
import { StoppedToast } from '@/features/player/ResumeUI';
import { RuqyahBar } from '@/features/adhkar/RuqyahListen';
import { watchPlayerHandover } from '@/features/adhkar/ruqyahPlayer';
import { DEFAULT_RECITER, canHear } from '@/features/player/access';
import { reciterById } from '@/data/reciters';
import { usePlayer } from '@/features/player/audioEngine';
import { useSettings } from '@/lib/store';
import { stopAdhan, useAdhan } from './adhan';
import { PRAYER_LABEL } from '@shared/prayer';
import { usePushAlert } from '@/lib/push';
import { requestPersistence } from '@/lib/persist';

const TABS: { to: string; label: string; icon: (p: { size?: number; strokeWidth?: number }) => React.ReactNode; end?: boolean; also?: string }[] = [
  { to: '/', label: 'Home', icon: MosqueIcon, end: true },
  { to: '/quran', label: "Qur'an", icon: MushafIcon },
  { to: '/listen', label: 'Listen', icon: Headphones },
  { to: '/lectures', label: 'Lectures', icon: Clapperboard },
  { to: '/prayer', label: 'Prayer', icon: MinaretIcon, also: '/fasting' },
  { to: '/adhkar', label: 'Adhkar', icon: TasbihIcon },
];

/* While the adhan plays, a way to stop it from any screen. */
function AdhanPill() {
  const playing = useAdhan((s) => s.playing);
  if (!playing) return null;
  return (
    <div role="status" className="glass fixed left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full py-1.5 pl-4 pr-1.5" style={{ top: 'calc(var(--safe-top) + 8px)' }}>
      <span className="text-[14px] font-semibold">{playing === 'preview' ? 'Adhan' : `Adhan · ${PRAYER_LABEL[playing].en}`}</span>
      <button type="button" onClick={() => stopAdhan()} className="press rounded-full bg-accent px-3 py-1.5 text-[13px] font-semibold text-accent-ink">Stop</button>
    </div>
  );
}

/* Notifications had stopped (Apple expired this phone's push connection, or
   the device was dropped) — the app just reconnected. Never let that be silent. */
function PushStoppedBanner() {
  const { stoppedAt, dismiss } = usePushAlert();
  if (!stoppedAt) return null;
  return (
    <div role="alert" className="glass fixed inset-x-3 z-50 mx-auto max-w-[560px] rounded-2xl p-3.5" style={{ top: 'calc(var(--safe-top) + 8px)' }}>
      <div className="text-[14px] font-semibold">Notifications had stopped</div>
      <p className="mt-0.5 text-[13px] leading-snug text-muted">
        iPhone ended this app’s notification connection{stoppedAt ? ` around ${new Date(stoppedAt).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}. It’s reconnected now — opening the app now and then keeps it alive.
      </p>
      <div className="mt-2 flex gap-2">
        <Link to="/settings/notifications" onClick={dismiss} className="press rounded-full bg-accent px-3 py-1.5 text-[13px] font-semibold text-accent-ink">See details</Link>
        <button type="button" onClick={dismiss} className="press rounded-full bg-fill px-3 py-1.5 text-[13px] font-semibold">OK</button>
      </div>
    </div>
  );
}

/* Shown only while an update is held back because audio is playing. */
function UpdatePill() {
  const ready = useUpdate((s) => s.ready);
  if (!ready) return null;
  return (
    <button type="button" onClick={() => location.reload()} className="glass press fixed left-1/2 z-50 -translate-x-1/2 rounded-full px-4 py-2 text-[13px] font-semibold text-accent"
      style={{ top: 'calc(var(--safe-top) + 8px)' }}>
      Update ready — tap to refresh
    </button>
  );
}

/* New screens start at the top; going back (POP) returns to where you were.
   The browser only restores the document's scroll, so the app scroller keeps
   its own memory per history entry. */
function useScrollMemory(key: string, pathname: string, navType: string) {
  const positions = useRef(new Map<string, number>());
  const current = useRef(key);
  const lastPath = useRef(pathname);
  useEffect(() => {
    const el = scroller();
    const save = () => positions.current.set(current.current, el.scrollTop);
    el.addEventListener('scroll', save, { passive: true });
    return () => el.removeEventListener('scroll', save);
  }, []);
  useLayoutEffect(() => {
    current.current = key;
    const samePage = lastPath.current === pathname;
    lastPath.current = pathname;
    if (navType !== 'POP') { if (!samePage) scrollPageTo(0); return; }
    const want = positions.current.get(key) ?? 0;
    /* The page may still be loading its content: keep trying (≈1s) until it's
       tall enough — unless you start scrolling yourself. */
    const el = scroller();
    let tries = 0, raf = 0;
    const stop = () => cancelAnimationFrame(raf);
    const go = () => { scrollPageTo(want); if (Math.abs(scrollTop() - want) > 2 && tries++ < 60) raf = requestAnimationFrame(go); };
    el.addEventListener('touchstart', stop, { passive: true, once: true });
    el.addEventListener('wheel', stop, { passive: true, once: true });
    go();
    return () => { stop(); el.removeEventListener('touchstart', stop); el.removeEventListener('wheel', stop); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function AppShell() {
  useApplyTheme();
  useNotificationSync();
  useCloudSync();
  usePlayAdhan();
  useNamePlace();
  // A hifz lesson closed mid-way: put the player's own repeat settings back (once settings have loaded).
  useEffect(() => {
    if (useSettings.persist.hasHydrated()) restoreHifzRepeat();
    else return useSettings.persist.onFinishHydration(() => restoreHifzRepeat());
  }, []);
  useEffect(() => { void requestPersistence(); trackResumePoints(); watchPlayerHandover(); }, []);
  const loc = useLocation();
  const gender = useSettings((s) => s.settings.gender);
  useEffect(() => {
    if (gender === 'female') return;
    const { settings, update } = useSettings.getState();
    if (!canHear(reciterById(settings.reciterId), gender)) update({ reciterId: DEFAULT_RECITER });
    const p = usePlayer.getState();
    if (p.status === 'idle' && !canHear(reciterById(p.reciterId), gender)) usePlayer.setState({ reciterId: DEFAULT_RECITER });
  }, [gender]);
  const navType = useNavigationType();
  useScrollMemory(loc.key, loc.pathname, navType);
  useEffect(() => { requestAnimationFrame(releaseStaleScrollLock); }, [loc.pathname]);
  // The reader is immersive: no tab bar while reading a surah/page.
  const onLectures = loc.pathname === '/lectures';
  const immersive = /^\/quran\/.+/.test(loc.pathname) || /^\/khatmah\/.+/.test(loc.pathname);
  return (
    <>
      {/* The one page scroller — see lib/scroll.ts for why the document doesn't scroll. */}
      <main id={SCROLL_ID} className="app-scroll"><Outlet /></main>
      {/* Lectures is full-screen video (and pauses other audio) — no player bars over it. */}
      {/* The hifz lesson and revision have their own listen controls (the bar would cover their save buttons). */}
      {!onLectures && !/^\/hifz\/(lesson|review)/.test(loc.pathname) && <><MiniPlayer /><RuqyahBar /></>}
      <UpdatePill />
      <PushStoppedBanner />
      <AdhanPill />
      <StoppedToast />
      {!immersive && (
        <nav aria-label="Main" className="glass fixed inset-x-3 z-40 mx-auto flex max-w-[560px] justify-around rounded-[26px] px-1 py-1.5"
          style={{ bottom: 'calc(var(--safe-bottom) + 8px)', height: 'var(--tabbar-h)' }}>
          {TABS.map(({ to, label, icon: Icon, end, also }) => (
            <NavLink key={to} to={to} end={end}
              // Tapping the tab you're already on scrolls it back to the top (as on iOS).
              onClick={() => { haptic(6); if (loc.pathname === to) scrollPageTo(0, true); }}
              className={({ isActive }) => `press flex flex-1 flex-col items-center justify-center gap-0.5 rounded-[20px] text-[11px] font-semibold transition-colors ${isActive || (also && loc.pathname.startsWith(also)) ? 'bg-fill text-accent' : 'text-muted'}`}>
              <Icon size={22} strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>
      )}
    </>
  );
}
